import React, { useEffect, useMemo, useRef } from 'react'

import {
  ContainsEditableProps,
  useEditor,
  FieldType,
  ModuleField
} from '@toasttab/sites-components'
import { t } from 'src/translations'

import {
  PaddingEnum,
  Image as ImageObject,
  VideoConfig
} from 'src/apollo/sites'
import { getSectionPaddingModule } from 'src/shared/components/common/editor_context/editableUtils'
import {
  checkIsAutoplayEnabled,
  checkIsControlsHidden,
  checkIsMuted,
  VideoConfigUtilsInput
} from 'src/shared/js/videoConfigUtils'
import { shouldApplyFirefoxEditorWorkaround } from '@toasttab/sites-components'

import { getImageUrl } from 'shared/components/common/Image'

import { resources } from 'config'

type Props = {
  imageObject?: ImageObject | null
  imageObjectPath?: string | null
  videoSrc: string
  videoConfig?: VideoConfig | null
  padding?: PaddingEnum | null
  videoDescription?: string | null
} & ContainsEditableProps

export const DO_PREFIX_REGEX = new RegExp(
  /^https:\/\/toast-sites(-prod)?.nyc3.cdn.digitaloceanspaces.com\//
)

const videoConfigToUtilsInput = (
  videoConfig: VideoConfig | null | undefined
): VideoConfigUtilsInput => {
  return {
    muted: videoConfig?.muted,
    autoplay: videoConfig?.autoplay,
    hideControls: videoConfig?.hideControls
  }
}

const VideoHero = ({
  editPath,
  videoSrc,
  videoConfig,
  videoDescription,
  padding = PaddingEnum.Medium,
  imageObject,
  imageObjectPath
}: Props) => {
  const { useEditableRef, isEditor } = useEditor()
  const maxAriaLen = 400
  const videoRef = useRef<HTMLVideoElement>(null)
  const applyWorkaround = shouldApplyFirefoxEditorWorkaround(isEditor)

  const videoConfigUtilsInput = useMemo(
    () => videoConfigToUtilsInput(videoConfig),
    [videoConfig]
  )
  const isControlsHidden = useMemo(
    () => checkIsControlsHidden(videoConfigUtilsInput),
    [videoConfigUtilsInput]
  )
  const isAutoplayEnabled = useMemo(
    () => checkIsAutoplayEnabled(videoConfigUtilsInput),
    [videoConfigUtilsInput]
  )
  const isMuted = useMemo(
    () => checkIsMuted(videoConfigUtilsInput),
    [videoConfigUtilsInput]
  )

  const { editableRef, pushSchema } = useEditableRef<HTMLDivElement>({
    name: 'hero section',
    displayName: t('video-hero.video-hero-section'),
    actions: ['move-up', 'move-down', 'duplicate', 'delete'],
    path: editPath,
    schema: {
      fields: [
        getSectionPaddingModule(editPath, padding),
        {
          path: `${editPath}.video`,
          type: FieldType.Video,
          displayName: t('video-hero.video'),
          value: videoSrc
        },
        {
          path: `${editPath}.videoConfig.captionFile`,
          type: FieldType.CaptionFile,
          displayName: t('video-hero.video-caption'),
          value: videoConfig?.captionFile
        },
        {
          path: imageObjectPath || `${editPath}.images[0]`,
          type: FieldType.Image,
          displayName: t('video-hero.poster-image'),
          value: imageObject?.src
        },
        {
          path: `${editPath}.ariaText`,
          displayName: t('video-hero.aria-text'),
          type: FieldType.Text,
          value: videoDescription || '',
          validation: { maxLen: maxAriaLen }
        },
        {
          path: `${editPath}.videoConfig.hideControls`,
          type: FieldType.Boolean,
          displayName: t('video-hero.hide-video'),
          value: isControlsHidden
        },
        // Autoplay is mandatory when controls are hidden, so we hide the toggle
        ...(!isControlsHidden
          ? [
              {
                path: `${editPath}.videoConfig.autoplay`,
                type: FieldType.Boolean,
                displayName: t('video-hero.play-auto'),
                value: isAutoplayEnabled
              } as ModuleField
            ]
          : []),
        // Muting is mandatory if video is autoplaying or controls are hidden, so we hide the toggle.
        ...(!isControlsHidden && !isAutoplayEnabled
          ? [
              {
                path: `${editPath}.videoConfig.muted`,
                type: FieldType.Boolean,
                displayName: t('video-hero.muted'),
                value: isMuted
              } as ModuleField
            ]
          : [])
      ]
    }
  })

  useEffect(() => {
    pushSchema()
  }, [pushSchema, videoConfig?.autoplay, videoConfig?.hideControls])

  useEffect(() => {
    // We must assign the `muted` attribute via DOM primatives.
    // Toggling muted on and off via JSX fails in Chrome.
    const video = videoRef.current
    if (video) {
      video.muted = isMuted
    }
  }, [isMuted, editPath])

  const videoDisplaySrc = useMemo(
    () => videoSrc?.replace(DO_PREFIX_REGEX, resources.publicAssetURL),
    [videoSrc]
  )

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.setAttribute('src', getImageUrl(videoDisplaySrc) as string)
      video.load()
    }
    return () => {
      /** We must carefully dismount the video element when dismounting the component
       *  (particularly in the editor), otherwise sound can play even after
       *  the video is removed from the DOM and the video will continue to take up memory.
       **/
      if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [videoDisplaySrc])

  const trackSrc = getImageUrl(videoConfig?.captionFile)

  return (
    <div
      ref={editableRef}
      className='hero'
      data-testid='video-hero-wrapper'
      style={applyWorkaround ? { position: 'relative' } : undefined}
    >
      {applyWorkaround && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1
          }}
          aria-hidden='true'
          data-testid='firefox-video-overlay'
        />
      )}
      <video
        ref={videoRef}
        src={getImageUrl(videoDisplaySrc)}
        id={`video-${editPath}`}
        crossOrigin='anonymous'
        poster={getImageUrl(
          imageObject?.displaySrc || imageObject?.src,
          imageObject?.displaySrc ? resources.imagePrefix : null
        )}
        muted={isMuted}
        autoPlay={isAutoplayEnabled}
        loop
        playsInline
        controls={!isControlsHidden}
        className='video'
        aria-label={videoDescription || t('video-hero.user-uploaded-video')}
        data-testid='video-hero'
        style={applyWorkaround ? { pointerEvents: 'none' } : undefined}
      >
        {trackSrc !== undefined ? (
          <track
            label='English'
            srcLang='en'
            kind='subtitles'
            src={trackSrc}
            default
          />
        ) : null}
      </video>
    </div>
  )
}

export default VideoHero
